import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Smartphone, Download, Upload, KeyRound, CheckCircle2, AlertTriangle,
  FileCheck, RefreshCw, Copy, Lock, ShieldCheck, Trash2, FlaskConical,
  BellRing, CalendarClock, Mail,
} from "lucide-react";
import { API } from "../../config";
import { useITPortalI18n } from "../../i18n/ITPortalI18nContext";
import { useDeferredEffect } from "../../hooks/useWalletEffects";

const fmt = (tpl, vars) =>
  tpl.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));

export const AppleWalletTab = () => {
  const { t, locale } = useITPortalI18n();
  const dateLocale = locale === 'IT' ? 'it-IT' : 'en-GB';

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cerFile, setCerFile] = useState(null);
  const [p12Password, setP12Password] = useState("");
  const [building, setBuilding] = useState(false);

  // Upload .p12 (final pipeline step)
  const [p12File, setP12File] = useState(null);
  const [uploadPwd, setUploadPwd] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem("it_token") || localStorage.getItem("admin_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/apple-wallet/status`, { headers: authHeaders() });
      setStatus(r.data);
    } catch (e) {
      console.error("apple-wallet status fetch failed", e);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (endpoint, filename) => {
    try {
      const r = await axios.get(`${API}/admin/apple-wallet/${endpoint}`, {
        responseType: "blob",
        headers: authHeaders(),
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(fmt(t.aw_toast_downloaded, { name: filename }));
    } catch (e) {
      toast.error(`${t.aw_toast_download_failed}: ${e.response?.data?.detail || e.message}`);
    }
  };

  // Helper to extract a useful error message from a Blob/JSON response
  const errorMsg = async (e) => {
    let msg = e.response?.data?.detail || e.message;
    if (e.response?.data instanceof Blob) {
      try { msg = JSON.parse(await e.response.data.text()).detail; } catch { /* ignore */ }
    }
    return msg;
  };

  const buildP12 = async () => {
    if (!cerFile) { toast.error(t.aw_toast_load_cer_first); return; }
    if (!p12Password || p12Password.length < 6) {
      toast.error(t.aw_toast_p12_pwd_short); return;
    }
    try {
      setBuilding(true);
      const form = new FormData();
      form.append("cer_file", cerFile);
      form.append("p12_password", p12Password);
      const r = await axios.post(`${API}/admin/apple-wallet/build-p12`, form, {
        responseType: "blob",
        headers: authHeaders(),
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "lojack_pass.p12";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t.aw_toast_p12_generated);
    } catch (e) {
      toast.error(`${t.aw_toast_p12_gen_failed}: ${await errorMsg(e)}`);
    } finally {
      setBuilding(false);
    }
  };

  const copyOpensslCmd = () => {
    const cmd = "openssl pkcs12 -export -out lojack_pass.p12 -inkey lojack_pass_private.key -in lojack_pass.pem -name \"LoJack Pass Type ID\"";
    navigator.clipboard.writeText(cmd);
    toast.success(t.aw_ssl_copied);
  };

  const uploadP12 = async () => {
    if (!p12File) { toast.error(t.aw_toast_select_p12); return; }
    if (!uploadPwd) { toast.error(t.aw_toast_enter_p12_pwd); return; }
    try {
      setUploading(true);
      const form = new FormData();
      form.append("p12_file", p12File);
      form.append("p12_password", uploadPwd);
      const r = await axios.post(`${API}/admin/apple-wallet/upload-p12`, form, {
        headers: authHeaders(),
      });
      toast.success(r.data.message || t.aw_toast_p12_uploaded);
      setP12File(null); setUploadPwd("");
      fetchStatus();
    } catch (e) {
      toast.error(`${t.aw_toast_upload_failed}: ${await errorMsg(e)}`);
    } finally {
      setUploading(false);
    }
  };

  const deleteP12 = async () => {
    if (!window.confirm(t.aw_upload_remove_confirm)) return;
    try {
      setDeleting(true);
      await axios.delete(`${API}/admin/apple-wallet/p12`, { headers: authHeaders() });
      toast.success(t.aw_toast_p12_removed);
      fetchStatus();
    } catch (e) {
      toast.error(`${t.aw_toast_remove_failed}: ${await errorMsg(e)}`);
    } finally {
      setDeleting(false);
    }
  };

  const [testingPass, setTestingPass] = useState(false);
  const downloadTestPass = async () => {
    try {
      setTestingPass(true);
      const r = await axios.get(`${API}/wallet/apple-pass-test/preview`, {
        responseType: "blob",
        headers: authHeaders(),
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/vnd.apple.pkpass" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "lojack_test_preview.pkpass";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t.aw_test_pass_ok);
    } catch (e) {
      toast.error(`${t.aw_test_failed || t.aw_sanity_failed}: ${await errorMsg(e)}`);
    } finally {
      setTestingPass(false);
    }
  };

  useDeferredEffect(() => { fetchStatus(); }, []);

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="it-apple-wallet-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gray-500/10 flex items-center justify-center">
            <Smartphone size={20} className="text-gray-300" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">{t.aw_title}</h2>
            <p className="text-gray-500 text-xs">{t.aw_subtitle}</p>
          </div>
        </div>
        <button onClick={fetchStatus} className="p-2 text-gray-500 hover:text-lojack-blue-400 transition-colors" data-testid="aw-refresh">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Status pipeline */}
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-6">
        <p className="text-gray-400 text-xs uppercase tracking-wider mb-4">{t.aw_pipeline_status}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { key: "csr_generated", label: t.aw_step_csr, ok: status?.csr_generated, sub: "lojack_pass.csr" },
            { key: "private_key_present", label: t.aw_step_key, ok: status?.private_key_present, sub: "lojack_pass_private.key (server-side)" },
            { key: "cert_p12_received", label: t.aw_step_p12, ok: status?.cert_p12_received, sub: "lojack_pass_cert.p12" },
          ].map(s => (
            <div key={s.key} className={`rounded-xl border p-4 ${s.ok ? "border-lojack-blue-500/30 bg-lojack-blue-500/5" : "border-lojack-clause-500/30 bg-lojack-clause-500/5"}`}>
              <div className="flex items-center gap-2 mb-1">
                {s.ok ? <CheckCircle2 size={14} className="text-lojack-blue-400" /> : <AlertTriangle size={14} className="text-lojack-clause-400" />}
                <p className="text-white text-sm font-medium">{s.label}</p>
              </div>
              <p className="text-gray-500 text-[11px] font-mono">{s.sub}</p>
            </div>
          ))}
        </div>
        {status?.pass_type_id && (
          <p className="text-gray-500 text-xs mt-4 font-mono">
            {t.aw_pass_type_id_label}: <span className="text-white">{status.pass_type_id}</span>
          </p>
        )}
      </div>

      <DurableStorageBlock status={status} t={t} dateLocale={dateLocale} />

      <SanityTestBlock authHeaders={authHeaders} disabled={!status?.cert_p12_received} t={t} />

      <CertMonitorBlock authHeaders={authHeaders} disabled={!status?.cert_p12_received} t={t} dateLocale={dateLocale} />

      {/* Downloads */}
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-6">
        <p className="text-gray-400 text-xs uppercase tracking-wider mb-4">{t.aw_downloads_title}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            onClick={() => downloadFile("csr", "lojack_pass.csr")}
            disabled={!status?.csr_generated}
            className="flex items-center justify-between p-4 bg-[#0a0a0a] border border-gray-800 rounded-xl hover:border-lojack-blue-500/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="aw-download-csr"
          >
            <div className="flex items-center gap-3">
              <FileCheck size={18} className="text-lojack-blue-400" />
              <div className="text-left">
                <p className="text-white text-sm font-medium">{t.aw_csr_label}</p>
                <p className="text-gray-500 text-[11px]">lojack_pass.csr</p>
              </div>
            </div>
            <Download size={16} className="text-gray-500" />
          </button>
          <button
            onClick={() => downloadFile("private-key", "lojack_pass_private.key")}
            disabled={!status?.private_key_present}
            className="flex items-center justify-between p-4 bg-[#0a0a0a] border border-lojack-clause-500/30 rounded-xl hover:border-lojack-clause-500/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="aw-download-key"
          >
            <div className="flex items-center gap-3">
              <KeyRound size={18} className="text-lojack-clause-400" />
              <div className="text-left">
                <p className="text-white text-sm font-medium flex items-center gap-1.5">{t.aw_private_key_label} <Lock size={11} className="text-lojack-clause-400" /></p>
                <p className="text-lojack-clause-500/80 text-[11px]">{t.aw_private_key_warn}</p>
              </div>
            </div>
            <Download size={16} className="text-gray-500" />
          </button>
        </div>
      </div>

      {/* Server-side .p12 builder */}
      <div className="bg-gradient-to-br from-lojack-blue-500/5 to-lojack-blue-500/5 border border-lojack-blue-500/20 rounded-2xl p-6" data-testid="aw-p12-builder">
        <div className="flex items-center gap-2 mb-2">
          <Upload size={16} className="text-lojack-blue-400" />
          <p className="text-white text-sm font-semibold">{t.aw_builder_title}</p>
        </div>
        <p className="text-gray-400 text-xs mb-5">{t.aw_builder_help}</p>
        <div className="space-y-3">
          <div>
            <label className="text-gray-400 text-[11px] uppercase tracking-wider">{t.aw_builder_cer_label}</label>
            <input
              type="file"
              accept=".cer,.pem,.crt"
              onChange={e => setCerFile(e.target.files?.[0] || null)}
              className="block w-full mt-1 text-sm text-gray-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-lojack-blue-500/15 file:text-lojack-blue-300 file:cursor-pointer hover:file:bg-lojack-blue-500/25"
              data-testid="aw-cer-input"
            />
            {cerFile && (
              <p className="text-lojack-blue-400 text-[11px] mt-1">{t.aw_builder_selected}: {cerFile.name} ({(cerFile.size / 1024).toFixed(1)} KB)</p>
            )}
          </div>
          <div>
            <label className="text-gray-400 text-[11px] uppercase tracking-wider">{t.aw_builder_pwd_label}</label>
            <input
              type="password"
              value={p12Password}
              onChange={e => setP12Password(e.target.value)}
              placeholder={t.aw_builder_pwd_placeholder}
              className="block w-full mt-1 px-4 py-2.5 bg-[#0a0a0a] border border-gray-700 rounded-xl text-white text-sm focus:border-lojack-blue-500 focus:outline-none"
              data-testid="aw-p12-password"
            />
          </div>
          <button
            onClick={buildP12}
            disabled={building || !cerFile || !p12Password}
            className="w-full bg-lojack-blue-500 hover:bg-lojack-blue-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
            data-testid="aw-build-p12-btn"
          >
            {building ? <><RefreshCw size={14} className="animate-spin" /> {t.aw_builder_generating}</> : <><Upload size={14} /> {t.aw_builder_generate_btn}</>}
          </button>
        </div>
      </div>

      {/* Upload .p12 final step */}
      <div
        className={`rounded-2xl p-6 border ${
          status?.cert_p12_received
            ? "bg-lojack-blue-500/5 border-lojack-blue-500/30"
            : "bg-lojack-clause-500/5 border-lojack-clause-500/30"
        }`}
        data-testid="aw-p12-upload-block"
      >
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className={status?.cert_p12_received ? "text-lojack-blue-400" : "text-lojack-clause-400"} />
            <p className="text-white text-sm font-semibold">{t.aw_upload_title}</p>
          </div>
          {status?.cert_p12_received && (
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded-full bg-lojack-blue-500/15 text-lojack-blue-400 text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1">
                <CheckCircle2 size={10} /> {t.aw_upload_uploaded_badge} ({(status.p12_size_bytes / 1024).toFixed(1)} KB)
              </span>
              <button
                onClick={deleteP12}
                disabled={deleting}
                className="p-1.5 text-gray-500 hover:text-red-400 transition-colors"
                title={t.aw_upload_remove_title}
                data-testid="aw-delete-p12-btn"
              >
                {deleting ? <RefreshCw size={14} className="animate-spin" /> : <Trash2 size={14} />}
              </button>
            </div>
          )}
        </div>

        <p className="text-gray-400 text-xs mb-5">{status?.cert_p12_received ? t.aw_upload_already_help : t.aw_upload_pending_help}</p>

        <div className="space-y-3">
          <div>
            <label className="text-gray-400 text-[11px] uppercase tracking-wider">{t.aw_upload_file_label}</label>
            <input
              type="file"
              accept=".p12,.pfx"
              onChange={e => setP12File(e.target.files?.[0] || null)}
              className="block w-full mt-1 text-sm text-gray-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-lojack-clause-500/15 file:text-lojack-clause-300 file:cursor-pointer hover:file:bg-lojack-clause-500/25"
              data-testid="aw-p12-upload-input"
            />
            {p12File && (
              <p className="text-lojack-clause-400 text-[11px] mt-1">{t.aw_builder_selected}: {p12File.name} ({(p12File.size / 1024).toFixed(1)} KB)</p>
            )}
          </div>
          <div>
            <label className="text-gray-400 text-[11px] uppercase tracking-wider">{t.aw_upload_pwd_label}</label>
            <input
              type="password"
              value={uploadPwd}
              onChange={e => setUploadPwd(e.target.value)}
              placeholder={t.aw_upload_pwd_placeholder}
              className="block w-full mt-1 px-4 py-2.5 bg-[#0a0a0a] border border-gray-700 rounded-xl text-white text-sm focus:border-lojack-clause-500 focus:outline-none"
              data-testid="aw-p12-upload-password"
            />
          </div>
          <button
            onClick={uploadP12}
            disabled={uploading || !p12File || !uploadPwd}
            className="w-full bg-lojack-clause-500 hover:bg-lojack-clause-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
            data-testid="aw-p12-upload-btn"
          >
            {uploading ? <><RefreshCw size={14} className="animate-spin" /> {t.aw_upload_verifying}</> : <><Upload size={14} /> {status?.cert_p12_received ? t.aw_upload_replace_btn : t.aw_upload_btn}</>}
          </button>
        </div>

        {status?.cert_p12_received && (
          <div className="mt-5 pt-5 border-t border-gray-800">
            <div className="flex items-center gap-2 mb-2">
              <FlaskConical size={14} className="text-lojack-blue-400" />
              <p className="text-white text-sm font-semibold">{t.aw_test_title}</p>
            </div>
            <p className="text-gray-400 text-xs mb-3">{t.aw_test_help}</p>
            <button
              onClick={downloadTestPass}
              disabled={testingPass}
              className="w-full bg-lojack-blue-500/20 hover:bg-lojack-blue-500/30 border border-lojack-blue-500/40 text-lojack-blue-300 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="aw-test-pass-btn"
            >
              {testingPass ? <><RefreshCw size={14} className="animate-spin" /> {t.aw_test_generating}</> : <><FlaskConical size={14} /> {t.aw_test_btn}</>}
            </button>
          </div>
        )}
      </div>

      {/* Manual openssl reference */}
      <details className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-4">
        <summary className="cursor-pointer text-gray-400 text-xs hover:text-white">{t.aw_ssl_title}</summary>
        <div className="mt-3 space-y-2 text-xs text-gray-400">
          <p>{t.aw_ssl_intro}</p>
          <ol className="list-decimal list-inside space-y-1 text-gray-500">
            <li>{t.aw_ssl_step1}
              <pre className="mt-1 bg-black/60 border border-gray-800 rounded-md p-2 font-mono text-[11px] overflow-x-auto text-lojack-blue-300">openssl x509 -inform DER -outform PEM -in apple.cer -out apple.pem</pre>
            </li>
            <li>{t.aw_ssl_step2}
              <div className="flex items-start gap-2 mt-1">
                <pre className="flex-1 bg-black/60 border border-gray-800 rounded-md p-2 font-mono text-[11px] overflow-x-auto text-lojack-blue-300">
{`openssl pkcs12 -export \\
  -out lojack_pass.p12 \\
  -inkey lojack_pass_private.key \\
  -in apple.pem \\
  -name "LoJack Pass Type ID"`}
                </pre>
                <button onClick={copyOpensslCmd} title={t.aw_ssl_copy_title} className="p-1.5 hover:bg-gray-800 rounded">
                  <Copy size={12} className="text-gray-500" />
                </button>
              </div>
            </li>
          </ol>
        </div>
      </details>
    </div>
  );
};

export default AppleWalletTab;


// ─── Durable storage status block ─────────────────────────────────────

const DurableStorageBlock = ({ status, t, dateLocale }) => {
  const ds = status?.durable_storage;
  const persistedInDb = !!ds?.persisted_in_db;
  const inDiskCache = !!status?.cert_p12_received;
  const inSync = persistedInDb === inDiskCache;
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString(dateLocale) : "—";
  const fmtSha = (sha) => sha ? sha.slice(0, 16) + "…" : "—";

  let banner = null;
  if (!persistedInDb && !inDiskCache) {
    banner = { tone: "neutral", text: t.aw_durable_none };
  } else if (persistedInDb && !inDiskCache) {
    banner = { tone: "info", text: t.aw_durable_only_db };
  } else if (!persistedInDb && inDiskCache) {
    banner = { tone: "warn", text: t.aw_durable_only_disk };
  } else {
    banner = { tone: "ok", text: t.aw_durable_ok };
  }

  const toneClasses = {
    ok: "bg-lojack-blue-500/5 border-lojack-blue-500/30 text-lojack-blue-300",
    info: "bg-lojack-blue-500/5 border-lojack-blue-500/30 text-lojack-blue-300",
    warn: "bg-lojack-clause-500/10 border-lojack-clause-500/40 text-lojack-clause-300",
    neutral: "bg-[#0a0a0a] border-gray-800 text-gray-400",
  };

  return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-6" data-testid="aw-durable-block">
      <div className="flex items-center gap-2 mb-4">
        <Lock size={14} className="text-lojack-blue-400" />
        <p className="text-white text-sm font-semibold">{t.aw_durable_title}</p>
        <span
          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ml-auto ${
            persistedInDb ? "bg-lojack-blue-500/15 text-lojack-blue-400 border border-lojack-blue-500/40" : "bg-gray-700/40 text-gray-400 border border-gray-700"
          }`}
        >
          {persistedInDb ? t.aw_durable_active : t.aw_durable_inactive}
        </span>
      </div>

      <div className={`rounded-xl border p-4 text-xs ${toneClasses[banner.tone]}`} data-testid="aw-durable-banner">
        {banner.text}
      </div>

      {persistedInDb && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-3">
            <p className="text-gray-500 uppercase tracking-wider text-[10px]">{t.aw_durable_uploaded_by}</p>
            <p className="text-white truncate" title={ds?.uploaded_by}>{ds?.uploaded_by || "—"}</p>
          </div>
          <div className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-3">
            <p className="text-gray-500 uppercase tracking-wider text-[10px]">{t.aw_durable_uploaded_at}</p>
            <p className="text-white">{fmtDate(ds?.uploaded_at)}</p>
          </div>
          <div className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-3">
            <p className="text-gray-500 uppercase tracking-wider text-[10px]">{t.aw_durable_sha256}</p>
            <p className="text-white font-mono">{fmtSha(ds?.sha256)}</p>
          </div>
        </div>
      )}

      {persistedInDb && !inSync && (
        <p className="text-lojack-clause-400 text-[11px] mt-3 flex items-center gap-1.5">
          <AlertTriangle size={10} /> {t.aw_durable_out_of_sync}
        </p>
      )}
    </div>
  );
};


// ─── End-to-end sanity test block ─────────────────────────────────────

const SanityTestBlock = ({ authHeaders, disabled, t }) => {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);

  const run = async () => {
    try {
      setRunning(true);
      setReport(null);
      const r = await axios.post(`${API}/admin/apple-wallet/sanity-test`, {}, { headers: authHeaders() });
      setReport(r.data);
      toast[r.data.ok ? "success" : "warning"](r.data.ok ? t.aw_sanity_ok : t.aw_sanity_warn);
    } catch (e) {
      toast.error(`${t.aw_sanity_failed}: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-6" data-testid="aw-sanity-block">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="flex items-center gap-2">
          <FlaskConical size={14} className="text-lojack-blue-400" />
          <p className="text-white text-sm font-semibold">{t.aw_sanity_title}</p>
        </div>
        <button
          onClick={run}
          disabled={running || disabled}
          className="px-4 py-2 bg-lojack-blue-500/15 hover:bg-lojack-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed border border-lojack-blue-500/40 text-lojack-blue-300 text-sm rounded-lg flex items-center gap-2"
          data-testid="aw-sanity-run-btn"
        >
          {running ? <><RefreshCw size={14} className="animate-spin" /> {t.aw_sanity_running}</> : <><FlaskConical size={14} /> {t.aw_sanity_run_btn}</>}
        </button>
      </div>

      <p className="text-gray-500 text-xs mb-4">{t.aw_sanity_help}</p>

      {report && (
        <div className="space-y-1.5" data-testid="aw-sanity-report">
          {report.checks.map((c, i) => (
            <div
              key={i}
              className={`rounded-lg border p-2.5 flex items-start gap-2 text-xs ${
                c.ok
                  ? "bg-lojack-blue-500/5 border-lojack-blue-500/30"
                  : "bg-lojack-clause-500/5 border-lojack-clause-500/30"
              }`}
            >
              {c.ok ? <CheckCircle2 size={14} className="text-lojack-blue-400 shrink-0 mt-0.5" /> : <AlertTriangle size={14} className="text-lojack-clause-400 shrink-0 mt-0.5" />}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium">{c.name}</p>
                {c.detail && <p className="text-gray-500 text-[11px] mt-0.5 break-all">{c.detail}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ─── Cert-expiry monitor block ────────────────────────────────────────

const CertMonitorBlock = ({ authHeaders, disabled, t, dateLocale }) => {
  const [monitor, setMonitor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/apple-wallet/cert-monitor/status`, { headers: authHeaders() });
      setMonitor(r.data);
    } catch (e) {
      console.warn("Cert monitor status failed", e);
    } finally {
      setLoading(false);
    }
  };

  useDeferredEffect(() => { if (!disabled) fetchStatus(); }, [disabled]);

  const runNow = async (force) => {
    if (!window.confirm(force ? t.aw_certmon_force_confirm : t.aw_certmon_run_confirm)) return;
    try {
      setRunning(true);
      const r = await axios.post(
        `${API}/admin/apple-wallet/cert-monitor/run-now${force ? "?force=true" : ""}`,
        {},
        { headers: authHeaders() },
      );
      if (r.data.alert_sent) {
        toast.success(fmt(t.aw_certmon_alert_sent_toast, { threshold: r.data.threshold, n: r.data.recipients?.length || 0 }));
      } else if (r.data.skip_reason === "healthy") {
        toast.info(fmt(t.aw_certmon_healthy_toast, { n: r.data.days_remaining }));
      } else if (r.data.skip_reason === "no_active_cert") {
        toast.warning(t.aw_certmon_no_active);
      } else {
        toast.info(t.aw_certmon_no_alert_needed);
      }
      await fetchStatus();
    } catch (e) {
      toast.error(`${t.aw_certmon_error}: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
    }
  };

  if (disabled) {
    return (
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-6 opacity-60" data-testid="aw-cert-monitor-block">
        <div className="flex items-center gap-2 mb-2">
          <BellRing size={14} className="text-gray-500" />
          <p className="text-gray-400 text-sm font-semibold">{t.aw_certmon_title}</p>
        </div>
        <p className="text-gray-500 text-xs">{t.aw_certmon_disabled}</p>
      </div>
    );
  }

  const cert = monitor?.cert;
  const days = cert?.days_remaining;
  const alertsSent = monitor?.alerts_sent || [];

  let healthTone = "ok";
  let healthLabel = "—";
  if (days === undefined || days === null) {
    healthTone = "neutral"; healthLabel = t.aw_certmon_state_waiting;
  } else if (days <= 0) {
    healthTone = "red"; healthLabel = t.aw_certmon_state_expired;
  } else if (days <= 14) {
    healthTone = "red"; healthLabel = `${t.aw_certmon_state_urgent} — ${fmt(t.aw_certmon_days_remaining, { n: days })}`;
  } else if (days <= 30) {
    healthTone = "amber"; healthLabel = `${t.aw_certmon_state_warning} — ${fmt(t.aw_certmon_days_remaining, { n: days })}`;
  } else if (days <= 60) {
    healthTone = "amber"; healthLabel = `${t.aw_certmon_state_notice} — ${fmt(t.aw_certmon_days_remaining, { n: days })}`;
  } else {
    healthTone = "ok"; healthLabel = `${t.aw_certmon_state_healthy} — ${fmt(t.aw_certmon_days_remaining, { n: days })}`;
  }
  const toneClasses = {
    ok: "bg-lojack-blue-500/10 border-lojack-blue-500/40 text-lojack-blue-300",
    amber: "bg-lojack-clause-500/10 border-lojack-clause-500/40 text-lojack-clause-300",
    red: "bg-red-500/10 border-red-500/40 text-red-300",
    neutral: "bg-gray-700/20 border-gray-700 text-gray-400",
  };

  return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-6" data-testid="aw-cert-monitor-block">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <BellRing size={14} className="text-lojack-blue-400" />
          <p className="text-white text-sm font-semibold">{t.aw_certmon_title}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchStatus}
            disabled={loading}
            title={t.aw_certmon_refresh}
            className="p-1.5 bg-[#1a1a1a] hover:bg-[#222] border border-gray-700 rounded-lg text-gray-400 hover:text-lojack-blue-400"
            data-testid="aw-cert-monitor-refresh"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            onClick={() => runNow(false)}
            disabled={running}
            className="px-3 py-1.5 bg-lojack-blue-500/15 hover:bg-lojack-blue-500/25 disabled:opacity-50 border border-lojack-blue-500/40 text-lojack-blue-300 text-xs rounded-lg flex items-center gap-1.5"
            data-testid="aw-cert-monitor-run-now"
          >
            <CalendarClock size={12} /> {t.aw_certmon_run_btn}
          </button>
          <button
            onClick={() => runNow(true)}
            disabled={running}
            className="px-3 py-1.5 bg-lojack-clause-500/10 hover:bg-lojack-clause-500/20 disabled:opacity-50 border border-lojack-clause-500/40 text-lojack-clause-300 text-xs rounded-lg flex items-center gap-1.5"
            title={t.aw_certmon_force_confirm}
            data-testid="aw-cert-monitor-force"
          >
            <Mail size={12} /> {t.aw_certmon_force_btn}
          </button>
        </div>
      </div>

      <p className="text-gray-500 text-xs mb-4">{t.aw_certmon_help}</p>

      <div className={`rounded-xl border p-4 ${toneClasses[healthTone]}`} data-testid="aw-cert-monitor-banner">
        <p className="text-[10px] uppercase tracking-wider opacity-70">{t.aw_certmon_status_label}</p>
        <p className="text-base font-semibold mt-0.5">{healthLabel}</p>
        {cert?.not_after_iso && (
          <p className="text-[11px] opacity-80 mt-1 font-mono">{t.aw_certmon_expires_on} {cert.not_after_iso.slice(0, 10)}</p>
        )}
      </div>

      {cert && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 text-xs">
          <div className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-3">
            <p className="text-gray-500 uppercase tracking-wider text-[10px]">Subject</p>
            <p className="text-white font-mono text-[11px] break-all">{cert.subject?.slice(0, 200) || "—"}</p>
          </div>
          <div className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-3">
            <p className="text-gray-500 uppercase tracking-wider text-[10px]">Issuer</p>
            <p className="text-white font-mono text-[11px] break-all">{cert.issuer?.slice(0, 200) || "—"}</p>
          </div>
        </div>
      )}

      {alertsSent.length > 0 && (
        <div className="mt-4">
          <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1.5">{t.aw_certmon_alerts_sent}</p>
          <div className="flex flex-wrap gap-1.5">
            {alertsSent.map(label => (
              <span key={label} className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-lojack-clause-500/15 text-lojack-clause-300 border border-lojack-clause-500/40">
                {label}
              </span>
            ))}
          </div>
        </div>
      )}

      {!monitor?.recipients_configured && (
        <p className="text-lojack-clause-400 text-[11px] mt-3 flex items-center gap-1.5">
          <AlertTriangle size={10} /> {t.aw_certmon_no_recipients}
        </p>
      )}

      {monitor?.last_run_at && (
        <p className="text-gray-600 text-[10px] mt-3 font-mono">
          {t.aw_certmon_last_check}: {new Date(monitor.last_run_at).toLocaleString(dateLocale)}
          {monitor.last_days_remaining !== null && ` · ${fmt(t.aw_certmon_days_remaining_short, { n: monitor.last_days_remaining })}`}
        </p>
      )}
    </div>
  );
};
